import { useMemo } from 'react'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'

/**
 * Generic response type for Redux hooks with generic data type
 * Makes data optional to match Redux toolkit patterns like ReduxObjectCache
 */
export type GenericHookResponse<TData = unknown> = {
    data?: TData
    isError: boolean
    isLoading: boolean
    error?: Readonly<SerializedErrorType>
    shouldInvalidateList?: boolean
}

/**
 * Response type for the multi-hook that preserves individual data types
 */
export type MultiHookResponse<THooks extends readonly GenericHookResponse<unknown>[]> = {
    /** Tuple of data from each hook, preserving their original types */
    data: { [K in keyof THooks]: THooks[K]['data'] }
    /** True if any of the hooks are in error state */
    isError: boolean
    /** True if any of the hooks are in loading state */
    isLoading: boolean
    /** Array of errors from each hook (undefined for hooks without errors) */
    errors: { [K in keyof THooks]: THooks[K]['error'] }
    /** True if any of the hooks should invalidate the list */
    shouldInvalidateList: boolean
}

/**
 * Hook that combines multiple Redux hooks into a single response, preserving their individual data types
 *
 * @param hookResponses - Array or tuple of responses from Redux hooks
 * @returns Combined hook response with consolidated loading and error states
 */
export function useMultiReduxHook<THooks extends readonly GenericHookResponse<unknown>[]>(
    hookResponses: [...THooks],
): MultiHookResponse<THooks> {
    return useMemo(() => {
        const isError = hookResponses.some((hook) => hook.isError)
        const isLoading = hookResponses.some((hook) => hook.isLoading)
        const shouldInvalidateList = hookResponses.some((hook) => hook.shouldInvalidateList)

        // Cast to maintain proper typing for errors
        const errors = hookResponses.map(
            (hook) => hook.error,
        ) as { [K in keyof THooks]: THooks[K]['error'] }

        // Cast to maintain proper typing while preserving each hook's data type
        const data = hookResponses.map(
            (hook) => hook.data,
        ) as { [K in keyof THooks]: THooks[K]['data'] }

        return {
            data,
            isError,
            isLoading,
            errors,
            shouldInvalidateList,
        }
    }, [hookResponses])
}

/**
 * Generic mutation hook response type that matches the structure returned by createMutationApi hooks
 */
type GenericMutationHookResponse = {
    inProgress: boolean
    mutate: unknown
    clearError?: () => void
    error?: Readonly<SerializedErrorType>
}

/**
 * Response type for the multi-mutate hook that provides consolidated controls
 */
export type MultiMutateHookResponse<THooks extends readonly GenericMutationHookResponse[]> = {
    /** Array of mutate functions from each hook, preserving their original types */
    mutate: { [K in keyof THooks]: THooks[K]['mutate'] }
    /** True if any of the mutation hooks are in progress */
    inProgress: boolean
    /** Function that clears errors from all mutation hooks */
    clearError: () => void
    /** Array of errors from each hook (undefined for hooks without errors) */
    errors: { [K in keyof THooks]: THooks[K]['error'] }
}

/**
 * Hook that combines multiple mutation hooks into a single response with consolidated controls
 *
 * @param mutationHooks - Array or tuple of mutation hook responses from createMutationApi
 * @returns Object with array of mutate functions and consolidated inProgress/clearError controls
 */
export function useMultiMutateReduxHook<THooks extends readonly GenericMutationHookResponse[]>(
    mutationHooks: [...THooks],
): MultiMutateHookResponse<THooks> {
    return useMemo(() => {
        const inProgress = mutationHooks.some((hook) => hook.inProgress)

        const mutate = mutationHooks.map(
            (hook) => hook.mutate,
        ) as { [K in keyof THooks]: THooks[K]['mutate'] }

        const errors = mutationHooks.map(
            (hook) => hook.error,
        ) as { [K in keyof THooks]: THooks[K]['error'] }

        const clearError = (): void => {
            mutationHooks.forEach((hook) => {
                if (hook.clearError) {
                    hook.clearError()
                }
            })
        }

        return {
            mutate,
            errors,
            inProgress,
            clearError,
        }
    }, [mutationHooks])
}
